import { useEffect, useRef, useState } from "react";
import cn from "clsx";
import "./SearchableSelect.css";
import { debounce } from "../common";
import Tooltip from "../Tooltip/Tooltip";
import { DynamicPositioningPortal } from "../DynamicPositioning/DynamicPositioning";
import { twMerge } from "tailwind-merge";

const defaultProps = {
  options: [],
  values: [],
};

const SearchableSelect = ({
  className = "",
  searchField = "",
  options = defaultProps.options,
  isMulti = false,
  values = defaultProps.values,
  handleChange,
  width = null,
  maxLimit = 0,
  loadOptions = null,
  creatable = false,
  maxHeight = 60,
  creatableValidation = null,
  disabled = false,
  isSearchable = true,
  handleInfiniteScroll = null,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef();
  const containerRef = useRef();
  const [showOptionsList, setShowOptionsList] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [searchedOptions, setSearchedOptions] = useState(null);
  const [isFetchingOptions, setIsFetchingOptions] = useState(false);
  const [creatableValueError, setCreatableValueError] = useState("");
  const [customInputs, setCustomInputs] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const parentRef = useRef();

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      handleInfiniteScroll && handleInfiniteScroll(activePage, setActivePage);
    }
  };

  const getOptions = () => {
    if (isFetchingOptions) {
      return (
        <div
          className="cursor-pointer h-9 flex items-center p-1 hover:bg-gray-100 px-3 text-gray-300 justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          Loading...
        </div>
      );
    }
    let firstOptionLabel = "";
    let optionsList = (filteredOptions || []).map((option, index) => {
      if (index === 0) firstOptionLabel = option.label;
      return (
        <div
          key={`${option.label}-${index}`}
          className={`cursor-pointer h-9 flex items-center p-1 hover:bg-gray-100 px-3  ${
            maxLimit > 0 &&
            values?.length === maxLimit &&
            "text-[#bcbbbb] !cursor-not-allowed"
          }`}
          value={JSON.stringify(option)}
        >
          {" "}
          {option.label}
        </div>
      );
    });

    if (
      inputValue &&
      creatable &&
      !(firstOptionLabel === inputValue.toLowerCase())
    ) {
      const creatableOption = (
        <div
          key="creatable"
          className="cursor-pointer h-9 flex items-center p-1 hover:bg-gray-100 px-3"
          value={JSON.stringify(inputValue.trim())}
          onClick={(e) => {
            setCustomInputs([...customInputs, inputValue.trim()]);
            e.stopPropagation();
            if (!creatableValueError) {
              handleChange(
                { label: inputValue.trim(), value: inputValue.trim() },
                "custom",
                [
                  ...(isMulti ? values : []),
                  { label: inputValue.trim(), value: inputValue.trim() },
                ]
              );
              setInputValue("");
              setShowOptionsList(false);
            }
          }}
        >
          {" "}
          Create "{inputValue}"
        </div>
      );

      optionsList.push(
        creatableValueError ? (
          <Tooltip content={creatableValueError}>{creatableOption}</Tooltip>
        ) : (
          creatableOption
        )
      );
    } else if (optionsList.length === 0) {
      optionsList.push(
        <div
          className="cursor-pointer h-9 flex items-center p-1 hover:bg-gray-100 px-3 text-gray-300 justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          No Options
        </div>
      );
    }

    return optionsList;
  };

  useEffect(() => {
    let validationError = creatableValidation
      ? creatableValidation(inputValue.trim())
      : customInputs.includes(inputValue.trim())
      ? "Can't create duplicate values"
      : "";
    setCreatableValueError(validationError);
  }, [creatableValidation, inputValue]);

  useEffect(() => {
    if (loadOptions && inputValue) {
      setIsFetchingOptions(true);
      debounce(() => {
        loadOptions(inputValue.trim()).then((data) => {
          setSearchedOptions(data);
          setIsFetchingOptions(false);
        });
      }, 500)();
    } else {
      const searchOptions = loadOptions ? searchedOptions : options;
      setSearchedOptions(
        (searchOptions || []).filter(({ label }) => {
          return label?.toLowerCase().includes(inputValue.toLowerCase());
        })
      );
    }
  }, [inputValue, setSearchedOptions, loadOptions, options]);

  useEffect(() => {
    setFilteredOptions(
      (searchedOptions || options || []).filter(
        (option) =>
          !values.find(
            ({ label, value }) =>
              option.label === label && option.value === value
          )
      )
    );
  }, [values, options, setFilteredOptions, searchedOptions]);

  useEffect(() => {
    containerRef.current?.scrollBy({ top: maxHeight, behaviour: "smooth" });
  }, [inputValue, values, filteredOptions, maxHeight, searchedOptions]);

  useEffect(() => {
    const outsideClickHandler = (e) => {
      // const selectContainer = document.getElementById(containerId);
      if (!(parentRef && parentRef.current.contains(e.target))) {
        setShowOptionsList(false);
      }
    };
    document.addEventListener("click", outsideClickHandler);
    return () => {
      document.removeEventListener("click", outsideClickHandler);
    };
  }, []);

  return (
    <div
      ref={parentRef}
      className={twMerge(
        cn(
          "relative text-sm border-[#d1d1d1] border-2 overflow-hidden focus:ring-0 focus:outline-none rounded-[4px] w-[300px] font-normal",
          {
            [`w-[${width}px]`]: width,
            "pointer-events-none !bg-[#F2F2F2]": disabled,
          },
          className
        )
      )}
      style={{ width }}
    >
      <div
        className="flex flex-row items-stretch justify-end"
        onClick={() => {
          inputRef?.current && inputRef?.current.focus();
          setShowOptionsList(!showOptionsList);
        }}
      >
        <div
          style={{ maxHeight: `${maxHeight}px` }}
          className={cn(
            "flex flex-wrap grow-1 shrink basis-11/12 p-2 items-center",
            {
              ["hide-scrollbar overflow-auto"]: maxHeight,
            },
            searchField
          )}
          ref={containerRef}
        >
          {isMulti &&
            values.map((option) => (
              <div
                className="bg-[#BEBEBE] mr-2 my-1 flex h-6 items-center rounded-[2px] text-xs"
                key={option.label}
              >
                <div className="p-1 pl-3 truncate">{option.label || ""}</div>
                <div
                  className="p-1 pr-2 select-none cursor-pointer hover:bg-gray-400 flex items-center h-full rounded-[2px]"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChange(
                      option,
                      "delete",
                      values.filter(
                        ({ value, label }) =>
                          !(label === option.label && value === option.value)
                      )
                    );
                  }}
                >
                  <svg
                    width="8"
                    height="8"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.5745 1L1 12.5745"
                      stroke="#000000"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M1.00024 1L12.5747 12.5745"
                      stroke="#000000"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>
            ))}
          <div
            className="before:content-[attr(data-value)] before:flex before:shrink before:grow before:flex-wrap flex flex-wrap items-center h-6 text-clip text-nowrap"
            data-value={inputValue}
          >
            {isInputFocused && (
              <div
                className="w-1 h-4 bg-black"
                style={{
                  animation: "cursor-blink 0.9s steps(2) infinite",
                  borderLeft: "2px solid white",
                }}
              ></div>
            )}
            {!Boolean(inputValue) && values.length === 0 && (
              <div className="text-[#d1d1d1]">
                {loadOptions ? "Search..." : "Select"}
              </div>
            )}
            {!isMulti && values.length === 1 && !!!inputValue && (
              <div className=""> {values[0]?.label}</div>
            )}
            {isSearchable && (
              <input
                className="absolute -left-[1000px]"
                ref={inputRef}
                type="text"
                value={inputValue}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setIsInputFocused(false)}
                onChange={(e) => {
                  !showOptionsList && setShowOptionsList(true);
                  setInputValue(e.target.value);
                }}
              />
            )}
          </div>
        </div>
        {maxLimit > 0 && (
          <div className="flex items-center text-xs m-1 text-[#adadad] ">
            {values?.length}&nbsp;/&nbsp;{maxLimit}
          </div>
        )}
        <div
          className={cn(
            "bg-white flex justify-around items-center basis-1/12",
            {
              "!bg-[#F2F2F2]": disabled,
            }
          )}
        >
          <div className="self-stretch w-[2px] bg-[#CCCCCC] h-4/5 m-auto "></div>
          <span
            className="p-[8px] text-[#d1d1d1]"
            onClick={(e) => {
              e.stopPropagation();
              setShowOptionsList(!showOptionsList);
            }}
          >
            <svg
              height="20"
              width="20"
              fill="currentColor"
              viewBox="0 0 20 20"
              focusable="false"
              stroke="currentColor"
            >
              <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
            </svg>
          </span>
        </div>
      </div>
      {showOptionsList && (
        <DynamicPositioningPortal parentRef={parentRef.current}>
          <div
            onScroll={handleScroll}
            className="w-full absolute left-0 bg-white z-30 rounded-md font-normal text-sm my-2 border-gray-300 border py-2 max-h-[200px] overflow-auto"
            onClick={(e) => {
              e.stopPropagation();
              const value = JSON.parse(e.target.getAttribute("value"));
              if (value) {
                setInputValue("");
                !isMulti && setShowOptionsList(false);
                isMulti
                  ? handleChange(value, "add", [...values, value])
                  : handleChange(value, "add", [value]);
              }
            }}
          >
            {getOptions()}
          </div>
        </DynamicPositioningPortal>
      )}
    </div>
  );
};

export default SearchableSelect;
